import { createSelector } from '@reduxjs/toolkit';
import { ConfigurationType } from 'data/types';
import {
  getDuctedPairZone,
  isDuctedPairComplete,
  selectActiveDuctedDesign,
  selectEligibleDuctedSystems
} from 'utils/ducted-design';
import {
  DuctedPairContext,
  DuctedPairContextState,
  DuctedPairLoads
} from 'utils/ducted-pair-context-types';
import { calculateZoneTotalBTUs } from 'utils/zone-outline';

export function buildDuctedPairContextBySystemId(
  design: ConfigurationType | null,
  eligibleSystemIds: ReadonlySet<string>
): Record<string, DuctedPairContext> {
  const contexts: Record<string, DuctedPairContext> = {};
  design?.condensers.forEach((condenser) => {
    const zone = getDuctedPairZone(condenser);
    const existingSystemId = zone?.existingSystemId;
    if (
      !zone ||
      !existingSystemId ||
      contexts[existingSystemId] ||
      !isDuctedPairComplete(condenser, eligibleSystemIds)
    ) {
      return;
    }
    contexts[existingSystemId] = {
      existingSystemId,
      condenserUniqueId: condenser.uniqueId,
      zoneUniqueId: zone.uniqueId,
      loads: calculateZoneTotalBTUs(zone),
      zoneItems: zone.zoneItems
    };
  });
  return contexts;
}

export const selectDuctedPairContextBySystemId = createSelector(
  [selectActiveDuctedDesign, selectEligibleDuctedSystems],
  (design, eligibleSystems) =>
    buildDuctedPairContextBySystemId(
      design,
      new Set(eligibleSystems.map((system) => system.uniqueId))
    )
);

export function selectDuctedPairContext(
  state: DuctedPairContextState,
  systemUniqueId: string | null | undefined
): DuctedPairContext | null {
  if (!systemUniqueId) return null;
  return selectDuctedPairContextBySystemId(state)[systemUniqueId] ?? null;
}

export function ductedPairLoadsFor(
  contexts: Record<string, DuctedPairContext>,
  systemUniqueId: string | null | undefined,
  fallback: DuctedPairLoads
): DuctedPairLoads {
  return (systemUniqueId && contexts[systemUniqueId]?.loads) || fallback;
}

export function selectDuctedPairLoads(
  state: DuctedPairContextState,
  systemUniqueId: string | null | undefined
): DuctedPairLoads {
  return ductedPairLoadsFor(
    selectDuctedPairContextBySystemId(state),
    systemUniqueId,
    state.project.totalBTUs
  );
}

export function selectDuctedPairCount(state: DuctedPairContextState): number {
  return Object.keys(selectDuctedPairContextBySystemId(state)).length;
}

export function ductedPairLoadsKey(
  contexts: Record<string, DuctedPairContext>
): string {
  return Object.values(contexts)
    .map(
      (context) =>
        `${context.existingSystemId}:${context.loads.cooling}/${context.loads.heating}`
    )
    .join(';');
}
